import { inject, Injectable } from '@angular/core';
import { catchError, finalize, map, Observable, shareReplay } from 'rxjs';
import { IAuthModel, AuthTokenType, IAuthResponseModel, IAuthResetPayload } from '../models';
import { DataStorageService } from './storage';
import { BaseService } from '@shared/core';
import { IResponse } from '../models';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private httpService = inject(BaseService);
  private storageService = inject(DataStorageService);

  private redirectUrl: string | null = null;
  private refreshInProgress$?: Observable<AuthTokenType>;
  private tokenExpiryTimer?: ReturnType<typeof setTimeout>;

  scheduleTokenExpiry(expiresIn: number): void {
    clearTimeout(this.tokenExpiryTimer);
    this.tokenExpiryTimer = setTimeout(() => {
      this.storageService.logout();
    }, expiresIn * 1000);
  }

  setRedirectUrl(url: string | null): void {
    this.redirectUrl = url;
  }

  getRedirectUrl(): string | null {
    return this.redirectUrl;
  }

  get canRefresh(): boolean {
    return this.refreshInProgress$ == null;
  }

  get authToken(): string | undefined {
    return this.storageService.getToken();
  }

  get isAuthenticated(): boolean {
    return !!this.authToken;
  }

  get authenticated$(): Observable<string | null> {
    return this.storageService.onTokenChanged();
  }

  onLogin(authData: IAuthModel): Observable<IResponse<IAuthResponseModel>> {
    return this.httpService.post('auth/token', authData).pipe(
      map((response: IResponse<IAuthResponseModel>) => {
        const value = response?.value;
        if (value?.accessToken) {
          this.storageService.setTokens(value.accessToken, value.refreshToken);
          if (value.expiresIn) this.scheduleTokenExpiry(value.expiresIn);
        }
        return response;
      }),
    );
  }

  onResetPassword(data: IAuthResetPayload): Observable<unknown> {
    return this.httpService.put('auth/reset-password', data);
  }

  onRefresh(): Observable<AuthTokenType> {
    // If refresh is already in progress, return the existing Observable
    if (this.refreshInProgress$) {
      return this.refreshInProgress$;
    }

    const refreshToken = this.storageService.getRefreshToken();

    // Otherwise, start a new refresh request
    this.refreshInProgress$ = this.httpService
      .post<IResponse<IAuthResponseModel>>('auth/refresh-token', {
        refreshToken: refreshToken,
      })
      .pipe(
        map((response: IResponse<IAuthResponseModel>) => {
          if (!response.isSuccess) throw response.errors;
          const { accessToken, refreshToken, expiresIn } = response.value;
          this.storageService.setTokens(accessToken, refreshToken);
          if (expiresIn) this.scheduleTokenExpiry(expiresIn);
          return accessToken;
        }),
        catchError(err => {
          clearTimeout(this.tokenExpiryTimer);
          this.storageService.logout();
          throw err;
        }),
        // shareReplay(1) ensures all subscribers get the same result and it replays once
        shareReplay(1),
        // finalize() lets us clean up after success or error
        finalize(() => {
          // Once the refresh finishes or fails, reset so future 401s can trigger another refresh
          this.refreshInProgress$ = undefined;
        }),
      );

    return this.refreshInProgress$;
  }

  onLogout(): Observable<unknown> {
    return this.httpService.post('auth/log-out').pipe(
      map(() => {
        clearTimeout(this.tokenExpiryTimer);
        this.storageService.logout();
      }),
    );
  }
}
